import {
   Button,
   Flex,
   Modal,
   SegmentedController,
   Table,
   Typography,
   useI18nContext,
} from "@procore/core-react";

import React, { useEffect, useState } from "react";
import { formatDateTime } from "../helper";
import type { RequestListData } from "@/react/components/tearsheets/request/types";

enum RequestState {
   VALID = "valid",
   INVALID = "invalid",
}

type ConflictModalProps = {
   isOpen: boolean;
   onClose: () => void;
   onSave: () => void;
   validRequests: RequestListData[];
   invalidRequests: Array<{ request: RequestListData; reason: string }>;
   selectedCount: number;
};

export const RequestConflictsModal: React.FC<ConflictModalProps> = ({
   isOpen,
   onClose,
   onSave,
   validRequests,
   invalidRequests,
   selectedCount,
}) => {
   // by default conflicts are shown
   const [selectedView, setSelectedView] = useState<RequestState>(RequestState.INVALID);
   const I18n = useI18nContext();

   useEffect(() => {
      setSelectedView(RequestState.INVALID);
   }, [isOpen]);

   return (
      <Modal
         open={isOpen}
         onClose={onClose}
         width="lg"
         style={{ maxHeight: "600px", maxWidth: "900px" }}
      >
         <Modal.Header style={{ padding: "10px 32px" }}>
            {I18n.t("views.company.workforce_planning.assignment.modal.conflicts")}
         </Modal.Header>
         <Modal.Body style={{ padding: "0px 32px" }}>
            <Flex direction="column" alignItems="center">
               <Typography>
                  {validRequests.length > 0
                     ? I18n.t("views.company.workforce_planning.assignment.modal.modal_help_text", {
                          valid_records: validRequests.length,
                          total_records: selectedCount,
                       })
                     : I18n.t(
                          "views.company.workforce_planning.assignment.bulk.edit.validation.batch_edit",
                       )}
               </Typography>

               {validRequests.length > 0 && (
                  <SegmentedController
                     aria-label="request-segmented-controller"
                     style={{ marginTop: "20px", width: "50%" }}
                  >
                     <SegmentedController.Segment
                        onClick={() => setSelectedView(RequestState.INVALID)}
                        selected={selectedView === RequestState.INVALID}
                     >
                        {I18n.t("views.company.workforce_planning.assignment.modal.conflicts")} (
                        {invalidRequests.length})
                     </SegmentedController.Segment>
                     <SegmentedController.Segment
                        onClick={() => setSelectedView(RequestState.VALID)}
                        selected={selectedView === RequestState.VALID}
                     >
                        {I18n.t("views.company.workforce_planning.assignment.modal.valid")} (
                        {validRequests.length})
                     </SegmentedController.Segment>
                  </SegmentedController>
               )}
            </Flex>

            <Table.Container
               style={{
                  marginTop: "20px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  overflowX: "auto",
               }}
            >
               <Table>
                  <Table.Header
                     style={{ whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 }}
                  >
                     <Table.HeaderRow>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.project")}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.start_date")}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.end_date")}
                        </Table.HeaderCell>
                        {selectedView === RequestState.INVALID && (
                           <Table.HeaderCell>
                              {I18n.t("views.company.workforce_planning.assignment.modal.conflict")}
                           </Table.HeaderCell>
                        )}
                     </Table.HeaderRow>
                  </Table.Header>
                  <Table.Body>
                     {selectedView === RequestState.INVALID &&
                        invalidRequests.map(({ request, reason }, index) => (
                           <Table.BodyRow key={`conflict-${request.id}-${index}`}>
                              <Table.BodyCell>
                                 <Table.TextCell>{request.project}</Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {request.start_day
                                       ? formatDateTime(request.start_day, {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {request.end_day
                                       ? formatDateTime(request.end_day, {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell style={{ color: "#d4061f" }}>
                                    {reason}
                                 </Table.TextCell>
                              </Table.BodyCell>
                           </Table.BodyRow>
                        ))}
                     {selectedView === RequestState.VALID &&
                        validRequests.map((data, index) => (
                           <Table.BodyRow key={`valid-${data.id}-${index}`}>
                              <Table.BodyCell>
                                 <Table.TextCell>{data.project}</Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {data.start_day
                                       ? formatDateTime(data.start_day, {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {data.end_day
                                       ? formatDateTime(data.end_day, {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                           </Table.BodyRow>
                        ))}
                  </Table.Body>
               </Table>
            </Table.Container>
         </Modal.Body>
         <Modal.Footer>
            <Modal.FooterButtons>
               <Button variant="secondary" onClick={onClose}>
                  {I18n.t("views.company.workforce_planning.cancel")}
               </Button>
               {validRequests.length > 0 && (
                  <Button variant="primary" onClick={onSave}>
                     {I18n.t("views.company.workforce_planning.save")}
                  </Button>
               )}
            </Modal.FooterButtons>
         </Modal.Footer>
      </Modal>
   );
};
